import type { ArrayForm, ObjectForm } from "@pdfking/forms"
import type { DestinationID, ImposeResult } from "@pdfking/imposition"
import { ImposeFiles } from "@pdfking/imposition"
import type { FileSlot, FilesSchema } from "@pdfking/imposition-form"
import { FilesToDestinations } from "@pdfking/imposition-form"
import { Init } from "@pdfking/reactivity/preact"
import type { PageNumber } from "@pdfking/renderer-api"
import { RecordMap, RecordMerge } from "@pdfking/tfunc"

import type { FileCollectionStore, Preset } from "#public"

import { range_for } from "./RangeFor"

type FileItem = ObjectForm<
  Record<"file_id" | "file_range" | "file_destination" | "file_scale", unknown>
>

export const CreatePresetController = (
  node: ObjectForm<Record<string, unknown>>,
  files_node: ObjectForm<Record<string, unknown>>,
  preset: Preset,
  store: FileCollectionStore
) =>
  Init({
    computed: {
      // The imposition alone — no file rows reach it, so adding a file or
      // editing a range doesn't re-run the pipeline.
      imposed(): ImposeResult | undefined {
        const value = this.node.value
        return value && this.preset.impose ? this.preset.impose(value) : undefined
      },
      // Which upload page fills each destination slot. Reads the store, so it
      // follows the split as it streams.
      file_map(): Record<DestinationID, Record<PageNumber, FileSlot>> {
        const files = this.files_node.value as FilesSchema | undefined
        return FilesToDestinations(
          files?.file_key ?? [],
          RecordMap(entry => entry.pages, this.store.files)
        )
      },
      // The pass-through has no imposition of its own: its pages are the rows'.
      result(): ImposeResult | undefined {
        if (this.preset.impose) return this.imposed
        const value = this.node.value as { filename: string } | undefined
        return value && this.files_node.value ? ImposeFiles(this.file_map, value) : undefined
      },
      // Both forms and the preset as one record — they travel in a single
      // payload (the route state, the produce request), each part validated by
      // its own form.
      input(): Record<string, unknown> {
        return RecordMerge(
          { preset: this.preset.id },
          this.node.input as Record<string, unknown>,
          this.files_node.input as Record<string, unknown>
        )
      },
      data(): Record<string, unknown> {
        return RecordMerge(
          { preset: this.preset.id },
          this.node.value as Record<string, unknown>,
          this.files_node.value as Record<string, unknown>
        )
      },
    },
    data: {
      node,
      files_node,
      preset,
      store,
    },
    methods: {
      file_key_node() {
        return this.files_node.results.file_key as ArrayForm<unknown>
      },
      // Every filename the current file rows reference — what the file store
      // should be reconciled against.
      file_names(): string[] {
        return ((this.input["file_key"] as Record<string, unknown>[]) ?? []).map(
          row => row["file_id"] as string
        )
      },
      file_inputs(): Record<string, unknown>[] {
        return this.file_key_node().results.map(item => item.input as Record<string, unknown>)
      },
      // The `file_key` rows with a new file appended, in url form — for the
      // caller to navigate with; nothing is written to the node.
      // Appended, not prepended: the rows read top-to-bottom in upload order,
      // and every other row op (+, duplicate) adds at the end too.
      file_rows_with(
        filename: string,
        page_count: number,
        destination?: string
      ): Record<string, unknown>[] {
        return [
          ...((this.input["file_key"] as Record<string, unknown>[]) ?? []),
          {
            file_id: filename,
            file_range: range_for(page_count),
            file_destination: destination,
          },
        ]
      },
      fill_range(filename: string, page_count: number) {
        for (const item of this.file_key_node().results) {
          const fields = (item as FileItem).results
          if (
            fields.file_id.input === filename &&
            (fields.file_range.input === "…" || !fields.file_range.input)
          ) {
            fields.file_range.validate(range_for(page_count))
          }
        }
      },
    },
  })

export type PresetController = ReturnType<typeof CreatePresetController>
